/* eslint-disable no-unreachable */
/* eslint-disable no-throw-literal */
/* eslint-disable no-unused-vars */

import axios from "axios";
import Cookies from "js-cookie";
import { set } from "lodash";
import qs from "qs";
import apiFactory from ".";
import { openNotificationError } from "../utils/Utils";

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

export default class BaseApi {
  DEFAULT_TIMEOUT = 20000;
  REACT_APP_AUTHENTICATION_WEB = process.env.REACT_APP_AUTHENTICATION_WEB;
  static hasMethodNotAllowed = false;

  constructor(baseUrl = process.env.REACT_APP_BASE_API_URL) {
    this.BASE_URL = baseUrl;
    this.$axiosInstance = this.initAxiosInstance();
  }

  retryRequest(originalRequest, newAccessToken) {
    if (!originalRequest.headers) {
      originalRequest.headers = {};
    }
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
    return axios(originalRequest).then((response) => response.data || response);
  }

  initAxiosInstance() {
    const axiosInstance = axios.create({
      baseURL: this.BASE_URL,
      withCredentials: false,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      timeout: this.DEFAULT_TIMEOUT,
    });

    axiosInstance.interceptors.request.use((request) => {
      if (BaseApi.hasMethodNotAllowed) {
        return;
      }

      const access_token = Cookies.get("access_token");

      if (!access_token) {
        return request;
      }

      set(request, "headers.Authorization", `Bearer ${access_token}`);
      return request;
    });

    axiosInstance.interceptors.response.use(
      (response) => {
        if (response.config.returnFullResponse) {
          return response;
        }

        if (response.config.responseType === "stream") {
          return response;
        }

        if (response?.data) {
          return response?.data;
        } else {
          return response;
        }
      },
      async (error) => {
        if (axios.isCancel(error)) {
          throw new Error("ERR_CANCELED");
        }

        const errorResponse = error?.response;
        const originalRequest = error?.config;

        if (errorResponse && errorResponse.status === 401) {
          if (!isRefreshing) {
            isRefreshing = true;
            const refreshToken = Cookies.get("refresh_token");

            return apiFactory.authApi
              .getAccessToken({ refreshToken })
              .then((result) => {
                if (result?.status === 400) {
                  isRefreshing = false;
                  Cookies.remove("access_token");
                  Cookies.remove("refresh_token");
                  localStorage.clear();
                  window.location.href =
                    this.REACT_APP_AUTHENTICATION_WEB + "/login";
                } else {
                  const newAccessToken = result?.data;
                  Cookies.set("access_token", newAccessToken);
                  isRefreshing = false;
                  onRefreshed(newAccessToken);
                  return this.retryRequest(originalRequest, newAccessToken);
                }
              })
              .catch((err) => {
                isRefreshing = false;
                Cookies.remove("access_token");
                Cookies.remove("refresh_token");
                localStorage.clear();
                // window.location.href = this.REACT_APP_AUTHENTICATION_WEB + "/login";
                throw err;
              });
          }

          return new Promise((resolve, reject) => {
            addRefreshSubscriber((newToken) => {
              this.retryRequest(originalRequest, newToken)
                .then(resolve)
                .catch(reject);
            });
          });
        }

        if (errorResponse && errorResponse.status === 500) {
          openNotificationError("Server Error");
          return;
        }

        if (
          error.code === "ECONNABORTED" &&
          error.message.includes("timeout") &&
          !["/api/preview/link"]?.includes(error?.config?.url)
        ) {
          openNotificationError("Request timed out");
          return;
        }

        if (typeof errorResponse?.data?.errors !== "string") {
          if (typeof errorResponse?.data === "object") {
            throw {
              status: errorResponse?.status,
              message: errorResponse?.data,
              success: false,
            };
          }
        } else {
          openNotificationError(errorResponse?.data?.errors);
        }
      }
    );
    return axiosInstance;
  }

  get(path, params, config = {}) {
    return this.$axiosInstance.get(path, {
      params,
      ...config,
    });
  }

  post(path, body, config) {
    const paramString = qs.stringify(config?.params, {
      arrayFormat: "repeat",
      charset: "utf-8",
    });
    const urlPost = paramString ? `${path}?${paramString}` : path;

    return this.$axiosInstance.post(urlPost, body, config);
  }

  put(path, body, config = {}) {
    return this.$axiosInstance.put(path, body, config);
  }

  patch(path, body) {
    return this.$axiosInstance.patch(path, body);
  }

  delete(path, config) {
    return this.$axiosInstance.delete(path, config);
  }

  deleteMany(path, body) {
    return this.$axiosInstance.delete(path, {
      data: body,
    });
  }
}
